import { createApi } from '@reduxjs/toolkit/dist/query/react';

import serverRoutes from '@app/utils/server-routes';

import {
  CreateDictionaryType,
  DictionariesType,
  DictionaryExportObjectTypeFinal,
  DictionaryType,
} from '@app/interfaces/slices-types/dictionaries-slice.type';

import fileDownload from 'js-file-download';

import { baseQuery } from '../baseQuery';

export const dictionariesApi = createApi({
  reducerPath: 'dictionariesApi',
  tagTypes: ['dictionaries', 'dictionary'],
  baseQuery: baseQuery,
  endpoints: (build) => ({
    getAllDictionariesList: build.query<DictionariesType[], void>({
      query: () => ({
        url: serverRoutes.dictionaries,
      }),
      transformResponse: (baseQueryReturnValue: { dictionaries: DictionariesType[] }) =>
        baseQueryReturnValue.dictionaries,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ dictionary_id }) => ({
                type: 'dictionaries' as const,
                dictionary_id,
              })),
              'dictionaries',
            ]
          : ['dictionaries'],
    }),
    getDictionary: build.query<DictionaryType, { id: string }>({
      query: (params) => ({
        url: serverRoutes.dictionary,
        params,
      }),
      providesTags: ['dictionary'],
    }),
    deleteDictionary: build.mutation({
      query: (params: { id: string }) => ({
        url: serverRoutes.dictionary,
        method: 'DELETE',
        params,
      }),
      invalidatesTags: ['dictionaries'],
    }),
    createDictionary: build.mutation<DictionaryType, CreateDictionaryType>({
      query: (body) => ({
        url: serverRoutes.dictionary,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['dictionaries'],
    }),
    editDictionary: build.mutation<
      DictionaryType,
      { body: CreateDictionaryType; params: { id: string } }
    >({
      query: ({ body, params }) => ({
        url: serverRoutes.dictionary,
        method: 'PUT',
        params,
        body,
      }),
      invalidatesTags: ['dictionaries', 'dictionary'],
    }),
    // import ex[port]
    importDictionary: build.mutation<DictionaryType, { body: any }>({
      query: ({ body }) => ({
        url: serverRoutes.dictionary_import,
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['dictionaries'],
    }),
    exportDictionary: build.query<DictionaryExportObjectTypeFinal, { params: string }>({
      query: ({ params }) => ({
        url: serverRoutes.dictionary_export,
        params: { ids: params },

        responseHandler: async (response) => {
          response.json().then((data) => {
            const name =
              data
                .map((d) => d.name)
                .join('&')
                .replace(/\s/g, '') + '.json';
            fileDownload(JSON.stringify(data), name);
          });
        },
        cache: 'no-cache',
      }),
    }),
  }),
});

export const {
  useGetAllDictionariesListQuery,
  useDeleteDictionaryMutation,
  useGetDictionaryQuery,
  useCreateDictionaryMutation,
  useEditDictionaryMutation,
  // export import
  useLazyExportDictionaryQuery,
  useImportDictionaryMutation,
} = dictionariesApi;
